// Refonte du suivi — geste 3 : valider et publier (lot 5).
//
// Une seule page : le contrôle (sur ce que la cliente recevra), l'aperçu de
// l'app (obligatoire avant de publier dans l'app), le choix app et/ou PDF,
// puis un bouton. Si la sortie échoue après la validation, la version reste
// « validée, pas encore publiée » et « Réessayer » renvoie la même chose.

import { useEffect, useRef, useState } from 'react';
import { ApercuApplication } from '../ClientAppPreviewModal';
import { confirmNarrativeProseReview } from '../services/weekMenus';
import { etatBoutonPublier } from './suiviEcran';
import { construireOverride, controlerVersion } from './services/suiviControle';
import {
  CANAUX_DOCUMENT,
  LIBELLES_CANAL_DOCUMENT,
  canauxEnAttente,
  lireValidation,
  programmeMgdActif,
  publierDansApp,
  remettreDocument,
  validerSuivi,
} from './services/suiviPublication';
import { enregistrerBrouillon, lireVersion } from './services/suiviVersions';

const DEPENDANCES = {
  controler: controlerVersion,
  mgdActif: programmeMgdActif,
  valider: validerSuivi,
  publier: publierDansApp,
  remettre: remettreDocument,
  enregistrer: enregistrerBrouillon,
  relire: lireVersion,
  relireValidation: lireValidation,
};

function Alerte({ alerte }) {
  return (
    <li className={alerte.gravite === 'bloquant' ? 'jrn-error suivi-alerte' : 'suivi-alerte'}>
      {alerte.texte}
      {alerte.phrase && <><br /><span className="suivi-alerte__phrase">Phrase : « {alerte.phrase} »</span></>}
      {alerte.aliment && !String(alerte.texte).includes(alerte.aliment) && (
        <><br /><span>Aliment : {alerte.aliment}</span></>
      )}
    </li>
  );
}

export default function ValiderPublier({
  client,
  ligne: ligneInitiale,
  validationInitiale = null,
  nomVersion = 'Cette version',
  db,
  onTermine,
  onRetour,
  dependances = {},
}) {
  const d = { ...DEPENDANCES, ...dependances };
  const options = db ? { db } : {};
  const [ligne, setLigne] = useState(ligneInitiale);
  const [validation, setValidation] = useState(validationInitiale);
  const [controle, setControle] = useState(null);
  const [erreurControle, setErreurControle] = useState(null);
  const [relance, setRelance] = useState(0);
  const [canaux, setCanaux] = useState({ app: true, document: false });
  const [canalDocument, setCanalDocument] = useState(validationInitiale?.document_canal || null);
  const [apercuOuvert, setApercuOuvert] = useState(false);
  const [apercuVu, setApercuVu] = useState(false);
  const [overrideCoche, setOverrideCoche] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const memoire = useRef({});
  const prenom = client?.prenom || client?.form?.prenom || 'la cliente';

  useEffect(() => {
    if (validation) return undefined;
    let vivant = true;
    (async () => {
      try {
        const mgd = await d.mgdActif(client?.id);
        const resultat = await d.controler({ client, ligne, mgd });
        if (vivant) { setControle(resultat); setErreurControle(null); }
      } catch (error) {
        if (vivant) setErreurControle(error?.message || 'Le contrôle n’a pas pu se faire.');
      }
    })();
    return () => { vivant = false; };
    // Le contrôle se relance quand la ligne change (relecture confirmée) ou sur demande.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ligne, relance, validation]);

  const canauxChoisis = ['app', 'document'].filter((canal) => canaux[canal]);
  const bouton = etatBoutonPublier({
    controle, canaux: canauxChoisis, apercuVu, canalDocument, overrideCoche, enCours,
  });

  async function sortir(validee) {
    let courante = validee;
    for (const canal of canauxEnAttente(courante)) {
      if (canal === 'app') {
        await d.publier({ client, validation: courante, memoire: memoire.current }, options);
      } else {
        await d.remettre({ client, validation: courante, canal: canalDocument, memoire: memoire.current }, options);
      }
      courante = await d.relireValidation(courante.id, options);
      setValidation(courante);
    }
    onTermine?.(courante);
  }

  async function validerEtPublier() {
    setEnCours(true);
    setErreur(null);
    try {
      const validee = await d.valider({
        ligne,
        controle,
        canaux: canauxChoisis,
        override: controle?.bloquant && overrideCoche ? construireOverride(controle.verdict) : null,
      }, options);
      setValidation(validee);
      await sortir(validee);
    } catch (error) {
      setErreur(error?.message || 'La publication n’a pas abouti.');
    } finally {
      setEnCours(false);
    }
  }

  async function reessayer() {
    setEnCours(true);
    setErreur(null);
    try {
      await sortir(await d.relireValidation(validation.id, options));
    } catch (error) {
      setErreur(error?.message || 'La publication n’a pas abouti.');
    } finally {
      setEnCours(false);
    }
  }

  async function confirmerRelecture() {
    const resultat = confirmNarrativeProseReview({ client, consultation: controle.lignePubliee, payload: controle.payload });
    if (!resultat.ok) { setErreur(resultat.error); return; }
    setEnCours(true);
    setErreur(null);
    try {
      const enregistree = await d.enregistrer({
        consultationId: ligne.id,
        revision: ligne.followup_edit_revision,
        changes: { editorial_overrides: { ...(ligne.editorial_overrides || {}), narrative_prose_review: resultat.consultation.editorial_overrides.narrative_prose_review } },
      }, options);
      setLigne(enregistree);
    } catch (error) {
      setErreur(error?.message || 'La confirmation n’a pas été enregistrée.');
    } finally {
      setEnCours(false);
    }
  }

  if (validation) {
    const restants = canauxEnAttente(validation);
    const documentRestant = restants.includes('document');
    return (
      <section className="jrn-block suivi-valider">
        <div className="jrn-block__head">
          <h3 className="jrn-block__title">{nomVersion} est validée, pas encore publiée.</h3>
        </div>
        <p className="jrn-block__intro">
          Reste à faire : {restants.map((canal) => (canal === 'app' ? `l’app de ${prenom}` : 'le PDF')).join(' et ') || 'rien'}.
          {' '}Le contenu validé ne change plus : « Réessayer » renvoie exactement la même chose.
        </p>
        {documentRestant && (
          <fieldset className="suivi-valider__canal">
            <legend>Le PDF</legend>
            {CANAUX_DOCUMENT.map((canal) => (
              <label key={canal} className="jrn-label">
                <input type="radio" name="canal-document" checked={canalDocument === canal} onChange={() => setCanalDocument(canal)} />
                {' '}{LIBELLES_CANAL_DOCUMENT[canal]}
              </label>
            ))}
          </fieldset>
        )}
        {erreur && <p className="jrn-error" role="alert">{erreur}</p>}
        <div className="jrn-actions">
          <button
            type="button"
            className="jrn-btn jrn-btn--primary"
            disabled={enCours || (documentRestant && !canalDocument)}
            onClick={() => reessayer()}
          >
            {enCours ? 'Publication…' : 'Réessayer'}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="jrn-block suivi-valider">
      <div className="jrn-block__head">
        <h3 className="jrn-block__title">Valider et publier {nomVersion}</h3>
        {onRetour && (
          <button type="button" className="jrn-btn jrn-btn--ghost" disabled={enCours} onClick={() => onRetour(ligne)}>
            Revenir aux modifications
          </button>
        )}
      </div>

      <h4 className="jrn-label">Contrôle de ce que {prenom} recevra</h4>
      {!controle && !erreurControle && <p className="jrn-block__intro">Contrôle en cours…</p>}
      {erreurControle && (
        <p className="jrn-error" role="alert">
          {erreurControle}{' '}
          <button type="button" className="jrn-btn jrn-btn--ghost" onClick={() => setRelance((n) => n + 1)}>Réessayer</button>
        </p>
      )}
      {controle?.erreurLivrable && <p className="jrn-error" role="alert">{controle.erreurLivrable}</p>}
      {controle && !controle.erreurLivrable && controle.alertes.length === 0 && (
        <p className="jrn-block__intro">Aucune alerte.</p>
      )}
      {controle && controle.alertes.length > 0 && (
        <ul className="suivi-valider__alertes">
          {controle.alertes.map((alerte, i) => <Alerte key={`${alerte.type}-${i}`} alerte={alerte} />)}
        </ul>
      )}
      {controle?.narratif?.required && !controle.narratif.confirmed && (
        <label className="jrn-label">
          <input type="checkbox" checked={false} disabled={enCours} onChange={() => confirmerRelecture()} />
          {' '}{controle.narratif.label}
        </label>
      )}
      {controle?.bloquant && controle.mgd && (
        <p className="jrn-block__intro">Programme MGD : pas de confirmation possible. Corrige le contenu dans « Revenir aux modifications ».</p>
      )}
      {controle?.confirmable && (
        <label className="jrn-label">
          <input type="checkbox" checked={overrideCoche} onChange={(event) => setOverrideCoche(event.target.checked)} />
          {' '}Je confirme ces alertes en connaissance de cause pour cette version.
        </label>
      )}

      <fieldset className="suivi-valider__canaux">
        <legend>Où l’envoyer ?</legend>
        <label className="jrn-label">
          <input type="checkbox" checked={canaux.app} onChange={(event) => setCanaux((c) => ({ ...c, app: event.target.checked }))} />
          {' '}Dans l’app de {prenom}
        </label>
        <label className="jrn-label">
          <input type="checkbox" checked={canaux.document} onChange={(event) => setCanaux((c) => ({ ...c, document: event.target.checked }))} />
          {' '}En PDF
        </label>
        {canaux.document && CANAUX_DOCUMENT.map((canal) => (
          <label key={canal} className="jrn-label suivi-valider__canal">
            <input type="radio" name="canal-document" checked={canalDocument === canal} onChange={() => setCanalDocument(canal)} />
            {' '}{LIBELLES_CANAL_DOCUMENT[canal]}
          </label>
        ))}
      </fieldset>

      {canaux.app && controle?.payload && (
        <div className="suivi-valider__apercu">
          <button
            type="button"
            className="jrn-btn jrn-btn--soft"
            onClick={() => { setApercuOuvert((o) => !o); setApercuVu(true); }}
          >
            {apercuOuvert ? 'Fermer l’aperçu' : 'Voir l’aperçu de l’app'}
          </button>
          {apercuOuvert && <ApercuApplication client={client} payload={controle.payload} consultation={controle.lignePubliee} />}
        </div>
      )}

      {erreur && <p className="jrn-error" role="alert">{erreur}</p>}
      <div className="jrn-actions suivi-valider__fin">
        <button type="button" className="jrn-btn jrn-btn--primary" disabled={!bouton.actif} onClick={() => validerEtPublier()}>
          {enCours ? 'Publication…' : 'Valider et publier'}
        </button>
        {!bouton.actif && bouton.raison && <span className="jrn-meta-chip">{bouton.raison}</span>}
      </div>
    </section>
  );
}
